import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Label } from "../../../components/ui/label";
import { InputText } from "../../../components/InputText";
import { api } from "../../../services/api";
import {
  Globe,
  Plus,
  Edit,
  Trash2,
  ArrowUp,
  ArrowDown,
  Image,
  Link,
  Eye,
  EyeOff,
} from "lucide-react";

interface SiteCarrosselItem {
  id: string;
  titulo: string;
  img: string;
  url: string | null;
  ordem: number;
  status: string;
  criado_em: string;
  alterado_em: string;
}

type ActiveTab = "carrossel";

export function Site() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("carrossel");

  // ── Carrossel state ─────────────────────────────────────
  const [slides, setSlides] = useState<SiteCarrosselItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<SiteCarrosselItem | null>(null);
  const [formTitulo, setFormTitulo] = useState("");
  const [formImg, setFormImg] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [formOrdem, setFormOrdem] = useState("0");
  const [formStatus, setFormStatus] = useState("A");
  const [savingSlide, setSavingSlide] = useState(false);

  // Modal excluir
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState<{
    id: string;
    titulo: string;
  } | null>(null);

  // ── Carregar slides ──────────────────────────────────────
  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      setLoading(true);
      const response = await api.get("/site/carrossel");
      setSlides(response.data);
    } catch {
      enqueueSnackbar("Erro ao carregar slides do carrossel", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Reordenar ────────────────────────────────────────────
  const moveSlide = async (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === slides.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const reordered = [...slides];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, removed);

    const updated = reordered.map((s, idx) => ({ ...s, ordem: idx + 1 }));
    setSlides(updated);

    try {
      await Promise.all(
        updated.map((s) =>
          api.put(`/site/carrossel/${s.id}`, { ordem: s.ordem })
        )
      );
      enqueueSnackbar("Ordem atualizada com sucesso", { variant: "success" });
    } catch {
      enqueueSnackbar("Erro ao atualizar ordem", { variant: "error" });
      await loadSlides();
    }
  };

  // ── Modal criar/editar ───────────────────────────────────
  const openCreateModal = () => {
    setEditingSlide(null);
    setFormTitulo("");
    setFormImg("");
    setFormUrl("");
    setFormOrdem(String((slides.length + 1)));
    setFormStatus("A");
    setIsModalOpen(true);
  };

  const openEditModal = (slide: SiteCarrosselItem) => {
    setEditingSlide(slide);
    setFormTitulo(slide.titulo);
    setFormImg(slide.img);
    setFormUrl(slide.url || "");
    setFormOrdem(String(slide.ordem));
    setFormStatus(slide.status);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSlide(null);
  };

  const handleSaveSlide = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTitulo.trim()) {
      enqueueSnackbar("O título é obrigatório", { variant: "warning" });
      return;
    }
    if (!formImg.trim()) {
      enqueueSnackbar("A URL da imagem é obrigatória", { variant: "warning" });
      return;
    }

    try {
      setSavingSlide(true);
      const payload = {
        titulo: formTitulo.trim(),
        img: formImg.trim(),
        url: formUrl.trim() || null,
        ordem: parseInt(formOrdem, 10) || 0,
        status: formStatus,
      };

      if (editingSlide) {
        await api.put(`/site/carrossel/${editingSlide.id}`, payload);
        enqueueSnackbar("Slide atualizado com sucesso", { variant: "success" });
      } else {
        await api.post("/site/carrossel", payload);
        enqueueSnackbar("Slide criado com sucesso", { variant: "success" });
      }

      closeModal();
      await loadSlides();
    } catch {
      enqueueSnackbar(
        editingSlide ? "Erro ao atualizar slide" : "Erro ao criar slide",
        { variant: "error" }
      );
    } finally {
      setSavingSlide(false);
    }
  };

  // ── Modal excluir ────────────────────────────────────────
  const openDeleteModal = (slide: SiteCarrosselItem) => {
    setSlideToDelete({ id: slide.id, titulo: slide.titulo });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSlide = async () => {
    if (!slideToDelete) return;

    try {
      await api.delete(`/site/carrossel/${slideToDelete.id}`);
      enqueueSnackbar("Slide excluído com sucesso", { variant: "success" });
      setIsDeleteModalOpen(false);
      setSlideToDelete(null);
      await loadSlides();
    } catch {
      enqueueSnackbar("Erro ao excluir slide", { variant: "error" });
    }
  };

  // ── Toggle status ────────────────────────────────────────
  const toggleStatus = async (slide: SiteCarrosselItem) => {
    const novoStatus = slide.status === "A" ? "I" : "A";
    try {
      await api.put(`/site/carrossel/${slide.id}`, { status: novoStatus });
      enqueueSnackbar(
        novoStatus === "A" ? "Slide ativado" : "Slide desativado",
        { variant: "success" }
      );
      await loadSlides();
    } catch {
      enqueueSnackbar("Erro ao alterar status do slide", { variant: "error" });
    }
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Globe className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Site
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie o conteúdo exibido no site público
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "carrossel"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("carrossel")}
        >
          Carrossel
        </button>
      </div>

      {/* Tab: Carrossel */}
      {activeTab === "carrossel" && (
        <div className="space-y-6">
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <Image className="w-5 h-5" />
                    Slides do Carrossel
                  </CardTitle>
                  <CardDescription>
                    Gerencie as imagens que aparecem no carrossel da página
                    inicial do site. Apenas slides com status{" "}
                    <strong>Ativo</strong> ficam visíveis.
                  </CardDescription>
                </div>
                <Button
                  onClick={openCreateModal}
                  className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Novo Slide
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : slides.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum slide cadastrado.</p>
                  <p className="text-sm mt-1">
                    Clique em <strong>Novo Slide</strong> para começar.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {slides.map((slide, index) => (
                    <div
                      key={slide.id}
                      className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/30 transition-colors"
                    >
                      {/* Preview imagem */}
                      <div className="w-20 h-14 rounded overflow-hidden flex-shrink-0 bg-muted border">
                        <img
                          src={slide.img}
                          alt={slide.titulo}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-foreground truncate">
                            {slide.titulo}
                          </p>
                          <Badge
                            variant={
                              slide.status === "A" ? "default" : "secondary"
                            }
                            className={
                              slide.status === "A"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            }
                          >
                            {slide.status === "A" ? "Ativo" : "Inativo"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Ordem: {slide.ordem}
                          </span>
                        </div>

                        {slide.url && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground truncate">
                            <Link className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{slide.url}</span>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {slide.img}
                        </p>
                      </div>

                      {/* Ações */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveSlide(index, "up")}
                          disabled={index === 0}
                          title="Mover para cima"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveSlide(index, "down")}
                          disabled={index === slides.length - 1}
                          title="Mover para baixo"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleStatus(slide)}
                          title={
                            slide.status === "A" ? "Desativar" : "Ativar"
                          }
                        >
                          {slide.status === "A" ? (
                            <Eye className="w-4 h-4 text-green-600" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(slide)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteModal(slide)}
                          title="Excluir"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal criar/editar slide */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-6">
              {editingSlide ? "Editar Slide" : "Novo Slide"}
            </h2>

            <form onSubmit={handleSaveSlide} className="space-y-4">
              <div>
                <Label htmlFor="slide-titulo">
                  Título <span className="text-red-500">*</span>
                </Label>
                <InputText
                  id="slide-titulo"
                  name="slide-titulo"
                  title=""
                  placeholder="Ex: Banner Principal"
                  value={formTitulo}
                  onChange={(e) => setFormTitulo(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="slide-img">
                  URL da Imagem <span className="text-red-500">*</span>
                </Label>
                <InputText
                  id="slide-img"
                  name="slide-img"
                  title=""
                  placeholder="https://... ou /caminho/imagem.png"
                  value={formImg}
                  onChange={(e) => setFormImg(e.target.value)}
                  className="mt-1"
                />
                {formImg && (
                  <div className="mt-2 rounded overflow-hidden border h-24 bg-muted">
                    <img
                      src={formImg}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="slide-url">URL de Destino</Label>
                <InputText
                  id="slide-url"
                  name="slide-url"
                  title=""
                  placeholder="https://... (opcional)"
                  value={formUrl}
                  onChange={(e) => setFormUrl(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link para onde o usuário será redirecionado ao clicar na
                  imagem.
                </p>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="slide-ordem">Ordem</Label>
                  <InputText
                    id="slide-ordem"
                    name="slide-ordem"
                    title=""
                    type="number"
                    placeholder="0"
                    value={formOrdem}
                    onChange={(e) => setFormOrdem(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="slide-status">Status</Label>
                  <select
                    id="slide-status"
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value)}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  disabled={savingSlide}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={savingSlide}
                  className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  {savingSlide
                    ? "Salvando..."
                    : editingSlide
                    ? "Salvar Alterações"
                    : "Criar Slide"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmar exclusão */}
      {isDeleteModalOpen && slideToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Confirmar Exclusão
            </h2>
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir o slide{" "}
              <strong className="text-foreground">
                {slideToDelete.titulo}
              </strong>
              ? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSlideToDelete(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteSlide}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
